import axiosInstance from './axiosConfig';
import { apiRequest } from './axiosConfig';
import { handleApiError } from '../../utils/errorHandling';

export interface RetentionProposal {
  type: 'downgrade' | 'pause' | 'support' | 'roadmap' | 'none';
  title: string | null;
  description: string | null;
}

export interface CancelFeedbackResponse {
  feedback_id: number;
  retention: RetentionProposal;
}

export const submitCancelFeedback = async (
  reasonCode: string,
  reasonText?: string
): Promise<CancelFeedbackResponse> => {
  try {
    const response = await axiosInstance.post('/v1/subscription/cancel/feedback', {
      reason_code: reasonCode,
      reason_text: reasonText,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, '解約理由の送信に失敗しました');
  }
};

export const acceptRetention = async (
  feedbackId: number,
  action: string
): Promise<void> => {
  try {
    await axiosInstance.post('/v1/retention/accept', {
      feedback_id: feedbackId,
      action,
    });
  } catch (error) {
    throw handleApiError(error, 'リテンション提案の受諾に失敗しました');
  }
};

export const cancelSubscriptionV2 = async (reasonCode?: string): Promise<{
  cancel_at: number;
  cancel_at_period_end: boolean;
  current_period_end: number;
}> => {
  try {
    const response = await axiosInstance.post('/v1/subscription/cancel', {
      reason_code: reasonCode,
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error, '解約処理に失敗しました');
  }
};

export const pauseSubscription = async (): Promise<{
  paused_at: string;
  pause_expires_at: string;
}> => {
  try {
    const response = await axiosInstance.post('/v1/subscription/pause');
    return response.data;
  } catch (error) {
    throw handleApiError(error, '一時停止に失敗しました');
  }
};

export const resumeSubscriptionV2 = async (): Promise<void> => {
  try {
    await axiosInstance.post('/v1/subscription/resume');
  } catch (error) {
    throw handleApiError(error, '再開に失敗しました');
  }
};

// --- Plan Change API ---

/**
 * 現行のプラン体系（personal/team/agency）。正本は backend の config/stripe.php。
 * 旧 config/plans.php 体系（free/starter/pro・features 配列・banner_generation 上限）は
 * Stripe に price を持たない死んだ体系なので、ここには持ち込まない。
 */
export interface PlanInfo {
  slug: string;
  name: string;
  price_monthly: number | null;
  monthly_credits: number | null;
  stripe_price_id_monthly: string | null;
  limits: {
    /** リーダーを除く追加メンバー数。-1 は無制限 */
    member_limit: number;
    /** -1 は無制限 */
    max_product_registrations: number;
  };
  is_current: boolean;
}

export interface DowngradePreview {
  current_plan: string;
  target_plan: string;
  lost_features: string[];
  limit_changes: {
    monthly_credits: { current: number | null; new: number | null };
    member_limit: { current: number; new: number };
    max_product_registrations: { current: number; new: number };
  };
}

export const fetchPlans = async (): Promise<{ plans: PlanInfo[]; current_plan: string }> => {
  try {
    const response = await axiosInstance.get('/v1/plans');
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'プラン一覧の取得に失敗しました');
  }
};

export const upgradeSubscription = async (planSlug: string): Promise<{ message: string; plan: string; plan_name: string }> => {
  try {
    const response = await axiosInstance.post('/v1/subscription/upgrade', { plan_slug: planSlug });
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'アップグレードに失敗しました');
  }
};

export const downgradeSubscription = async (planSlug: string, reason?: string): Promise<{ message: string }> => {
  try {
    const response = await axiosInstance.post('/v1/subscription/downgrade', { plan_slug: planSlug, reason });
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'ダウングレードに失敗しました');
  }
};

export const fetchDowngradePreview = async (planSlug: string): Promise<DowngradePreview> => {
  try {
    const response = await axiosInstance.get('/v1/subscription/downgrade/preview', { params: { plan_slug: planSlug } });
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'ダウングレード情報の取得に失敗しました');
  }
};

// --- Account Deletion API ---

export interface DeletePrecheck {
  can_delete: boolean;
  blockers: Array<{
    type: string;
    message: string;
    action_url: string;
    action_label: string;
  }>;
}

export const fetchDeletePrecheck = async (): Promise<DeletePrecheck> => {
  try {
    const response = await axiosInstance.get('/v1/account/delete/precheck');
    return response.data;
  } catch (error) {
    throw handleApiError(error, '前提条件の確認に失敗しました');
  }
};

export const requestAccountDeletion = async (password: string, reason?: string): Promise<{
  message: string;
  hard_delete_at: string;
}> => {
  try {
    const response = await axiosInstance.post('/v1/account/delete', { password, reason });
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'アカウント削除に失敗しました');
  }
};

// --- Checkout & Subscription Status API ---

export interface CheckoutRequest {
  plan: 'personal' | 'team' | 'agency';
  billing_cycle?: 'monthly' | 'yearly';
}

export interface CheckoutResponse {
  checkout_url: string;
}

export interface SubscriptionStatusResponse {
  has_subscription: boolean;
  plan: string | null;
  status: string | null;
  current_period_end: string | null;
}

export const createCheckout = async (data: CheckoutRequest): Promise<CheckoutResponse> => {
  try {
    const response = await apiRequest.post<CheckoutResponse>('subscriptions/checkout', data);
    return response;
  } catch (error) {
    throw handleApiError(error, 'チェックアウトの作成に失敗しました');
  }
};

export const getSubscriptionStatus = async (): Promise<SubscriptionStatusResponse> => {
  try {
    const response = await apiRequest.get<SubscriptionStatusResponse>('subscriptions/status');
    return response;
  } catch (error) {
    throw handleApiError(error, 'サブスクリプション状態の取得に失敗しました');
  }
};
